import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Form, Alert, Table, Modal } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter } from '../../../components/common/FilterComponent';
import DeleteConfirmationModal from '../../../components/common/DeleteConfirmationModal';
import { actionBaseService, Action } from '../../../services/actionBaseService';
import { itemBaseService, Item } from '../../../services/itemBaseService';
import { questionBaseService, Question } from '../../../services/questionBaseService';
import { areaService, Area } from '../../../services/areaService';

// Types
interface NewAction {
    title: string;
    description: string;
}

const QuestionActionsPage: React.FC = () => {
    const { questionId, itemId } = useParams<{ questionId: string; itemId: string }>();
    const history = useHistory();
    const [question, setQuestion] = useState<Question | null>(null);
    const [item, setItem] = useState<Item | null>(null);
    const [actions, setActions] = useState<Action[]>([]);
    const [areas, setAreas] = useState<Area[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showNewActionModal, setShowNewActionModal] = useState<boolean>(false);
    const [showEditActionModal, setShowEditActionModal] = useState<boolean>(false);
    const [selectedAction, setSelectedAction] = useState<Action | null>(null);
    const [formData, setFormData] = useState<NewAction>({
        title: '',
        description: ''
    });
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'action'>('action');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [selectedActionForDelete, setSelectedActionForDelete] = useState<Action | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const filterOptions: FilterOption[] = [
        { value: 'action', label: 'Ação' },
    ];

    const fetchActions = async (filters?: { titulo?: string }) => {
        try {
            setFilterLoading(true);
            setError(null);

            const actionsResponse = await actionBaseService.getActions({
                page: 0,
                size: 100, // Get a reasonable number of actions
                itemBaseId: itemId,
                ...filters
            });
            setActions(actionsResponse.content);
        } catch (err) {
            setError('Erro ao carregar ações. Por favor, tente novamente.');
            console.error('Error fetching actions:', err);
        } finally {
            setFilterLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch question details
                const questionData = await questionBaseService.getQuestionById(questionId);
                setQuestion(questionData);

                // Fetch areas
                const areasData = await areaService.getAreas();
                setAreas(areasData);

                // Fetch item details
                const itemData = await itemBaseService.getItemById(itemId);
                setItem(itemData);

                // Fetch actions for this item
                await fetchActions();

            } catch (err) {
                setError('Erro ao carregar dados. Por favor, tente novamente.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [questionId, itemId]);

    const handleBack = () => {
        history.push(`/admin/questoes/${questionId}/itens`);
    };

    const handleNewActionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddNewAction = async () => {
        if (formData.title.trim() && formData.description.trim() && item) {
            try {
                setSubmitting(true);
                const newAction = await actionBaseService.createAction({
                    titulo: formData.title.trim(),
                    descricao: formData.description.trim(),
                    itemBaseId: itemId,
                    tipoBase: 'KATRU'
                });

                setActions(prev => [...prev, newAction]);
                setFormData({
                    title: '',
                    description: ''
                });
                setShowNewActionModal(false);
            } catch (err) {
                setError('Erro ao criar ação. Por favor, tente novamente.');
                console.error('Error creating action:', err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleEditClick = (action: Action) => {
        setSelectedAction(action);
        setFormData({
            title: action.titulo,
            description: action.descricao
        });
        setShowEditActionModal(true);
    };

    const handleUpdateAction = async () => {
        if (selectedAction && formData.title.trim() && formData.description.trim()) {
            try {
                setSubmitting(true);
                const updatedAction = await actionBaseService.updateAction(selectedAction.acaoBaseId!, {
                    titulo: formData.title.trim(),
                    descricao: formData.description.trim(),
                    tipoBase: 'KATRU',
                    itemBaseId: itemId,
                });

                setActions(prevActions =>
                    prevActions.map(action =>
                        action.acaoBaseId === selectedAction.acaoBaseId
                            ? updatedAction
                            : action
                    )
                );
                setShowEditActionModal(false);
                setSelectedAction(null);
                setFormData({
                    title: '',
                    description: ''
                });
            } catch (err) {
                setError('Erro ao atualizar ação. Por favor, tente novamente.');
                console.error('Error updating action:', err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleDeleteClick = (action: Action) => {
        setSelectedActionForDelete(action);
        setShowDeleteModal(true);
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setSelectedActionForDelete(null);
        setIsDeleting(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedActionForDelete) return;

        try {
            setIsDeleting(true);
            await actionBaseService.deleteAction(selectedActionForDelete.acaoBaseId!);
            setActions(prev => prev.filter(action => action.acaoBaseId !== selectedActionForDelete.acaoBaseId));
            handleDeleteModalClose();
        } catch (err) {
            setError('Erro ao remover ação. Por favor, tente novamente.');
            console.error('Error deleting action:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleModalClose = () => {
        setShowNewActionModal(false);
        setShowEditActionModal(false);
        setSelectedAction(null);
        setFormData({
            title: '',
            description: ''
        });
    };

    const getAreaName = (areaId: string) => {
        return areas.find(area => area.area_id === areaId)?.nome || 'Área não encontrada';
    };

    // Function to apply filters (called when user clicks search or presses Enter)
    const handleFilterApply = async (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'action';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');

        // Apply the filter to backend
        await applyFiltersToBackend([...updatedFilters, newAppliedFilter]);
    };

    // Function to remove a specific filter
    const handleFilterRemove = async (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
        await applyFiltersToBackend(updatedFilters);
    };

    // Function to apply filters to backend
    const applyFiltersToBackend = async (filters: AppliedFilter[]) => {
        // Prepare filters for backend
        const backendFilters: { titulo?: string } = {};

        if (filters.length > 0) {
            const actionFilter = filters.find(f => f.type === 'action');
            if (actionFilter) {
                backendFilters.titulo = actionFilter.value;
            }
        }

        // Fetch filtered data from backend
        await fetchActions(backendFilters);
    };

    // Function to clear filters
    const handleFilterClear = async () => {
        setFilterText('');
        setAppliedFilters([]);
        // Fetch all data without filters
        await fetchActions();
    };

    // No client-side filtering needed since we're using backend filtering
    const displayActions = actions;

    if (loading) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    if (error || !question || !item) {
        return (
            <div className="container-fluid p-4">
                <Alert variant="danger">
                    {error || 'Dados não encontrados'}
                </Alert>
                <Button variant="primary" onClick={handleBack}>
                    Voltar para Itens
                </Button>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={handleBack}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="dashboard-header mb-4 d-flex flex-row justify-content-between">
                <h2 className="mb-0 text-start">Gerenciar Ações do Item</h2>
                <Button variant="primary" onClick={() => setShowNewActionModal(true)}>
                    Adicionar Ação
                </Button>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <p className="text-muted mb-2">
                        Área: {getAreaName(question.areaId)}
                    </p>
                    <p className="text-muted mb-2">
                        Questão: {question.titulo}
                    </p>
                    <p className="text-muted mb-2">
                        Item: {item.titulo}
                    </p>
                </div>
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'action')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'action' ? 'ação' : 'status'}...`}
                    />
                </div>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {filterLoading && (
                <div className="text-center p-3">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            )}

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Título</th>
                            <th>Descrição</th>
                            <th>Gerenciar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayActions.map(action => (
                            <tr key={action.acaoBaseId || `action-${action.titulo}`}>
                                <td>{action.titulo}</td>
                                <td>{action.descricao}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleEditClick(action)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteClick(action)}
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {actions.length === 0 && (
                <div className="text-center text-muted p-4">
                    Nenhuma ação configurada para este item.
                </div>
            )}

            {/* Add Action Modal */}
            <Modal show={showNewActionModal} onHide={handleModalClose}>
                <Modal.Header>
                    <Modal.Title>Adicionar Ação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Título</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleNewActionChange}
                            placeholder="Digite o título da ação"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleNewActionChange}
                            rows={3}
                            placeholder="Digite a descrição da ação"
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddNewAction}
                        disabled={!formData.title.trim() || !formData.description.trim() || submitting}
                    >
                        {submitting ? 'Adicionando...' : 'Adicionar Ação'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Action Modal */}
            <Modal show={showEditActionModal} onHide={handleModalClose}>
                <Modal.Header>
                    <Modal.Title>Editar Ação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Título</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleNewActionChange}
                            placeholder="Digite o título da ação"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleNewActionChange}
                            rows={3}
                            placeholder="Digite a descrição da ação"
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpdateAction}
                        disabled={!formData.title.trim() || !formData.description.trim() || submitting}
                    >
                        {submitting ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                onConfirm={handleConfirmDelete}
                title="Confirmar Exclusão"
                message={`Tem certeza que deseja excluir a ação "${selectedActionForDelete?.titulo}"?`}
                warningMessage=""
                entityName="ação"
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default QuestionActionsPage; 